import * as React from 'react'
import { IsMobileContext } from '../../common/Context'
import { TextAreaProps, TextAreaState, ActionTypes, TextAreaActionTypes } from './types'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import Check from '../../images/check.svg'
import SmallX from '../../images/x_small.svg'
import './style.scss'

const TextArea: React.FC<TextAreaProps> = ({
  field,
  placeholder,
  required,
  onChange,
  className,
  revalidate
}) => {
  const {t} = useTranslation()
  const { isMobile } = React.useContext(IsMobileContext)

  const textAreaInitialState: TextAreaState = {
    value: '',
    isValid: !required,
    touched: false,
  }

  const textAreaReducer = (state: TextAreaState, action: TextAreaActionTypes) => {
    switch (action.type) {
    case ActionTypes.TEXTAREA_CHANGE:
      return {
        ...state,
        value: action.value,
        isValid: action.isValid,
      }
    case ActionTypes.TEXTAREA_BLUR:
      return {
        ...state,
        touched: true,
      }
    default:
      return state
    }
  }

  const [textAreaState, dispatch] = React.useReducer(textAreaReducer, textAreaInitialState)
  const [bgClass, setBgClass] = React.useState('bgBlurred')

  React.useEffect(() => {
    if (revalidate) {
      dispatch({ type: ActionTypes.TEXTAREA_BLUR })
    }
  }, [revalidate])

  React.useEffect(() => {
    if (textAreaState.touched) {
      onChange(field, textAreaState.value, textAreaState.isValid)
    }
  }, [textAreaState, onChange, field])

  const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.target.value
    const validationRules = [
      required && ((value: string) => value.trim().length > 0),
    ]
      .filter(Boolean) as ((value: string) => boolean)[]
    const isValid = validationRules.every(rule => rule(text))

    dispatch({ type: ActionTypes.TEXTAREA_CHANGE, value: text, isValid })
  }

  const focusHandler = () => {
    setBgClass('bgFocused')
  }

  const blurHandler = () => {
    if ((textAreaState.value.trim().length === 0 && textAreaState.isValid) || !textAreaState.isValid) {
      setBgClass('bgBlurred')
    }
    dispatch({ type: ActionTypes.TEXTAREA_BLUR })
  }

  const iconSize = isMobile ? '4vw' : '1vw'
  const validatedOk = textAreaState.value.trim().length > 0 && textAreaState.isValid && textAreaState.touched
  const validatedError = !textAreaState.isValid && textAreaState.touched

  return (
    <div className={`TextArea ${className || ''}`}>
      <div 
        className={`FontS w-full
          ${bgClass} hover:bgFocused
          flex items-center
          ${validatedError ? 'border-b border-accent -mb-px' : ''}`}
        style={{padding: iconSize}}
      >
        <div className="flex-grow relative">
          <textarea 
            className="focus:outline-none bg-transparent placeholder-black w-full resize-none"
            rows={5}
            value={textAreaState.value}
            required={required}
            onChange={changeHandler}
            onFocus={focusHandler}
            onBlur={blurHandler}
          />
          {textAreaState.value.length === 0 ? (
            <div className="absolute top-0 pointer-events-none">
              {placeholder}
              {!required && <span className="text-gray-50"> ({t('common:optional')})</span>}
            </div>
          ) : null}
        </div>
        {validatedError && (
          <SmallX style={{width: iconSize, height: iconSize, marginLeft: iconSize}} />
        )}
        {validatedOk && (
          <Check style={{width: iconSize, height: iconSize, marginLeft: iconSize}} />
        )}
      </div>
    </div>
  )
}

export default TextArea
